import React, { useCallback, useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import moment from 'moment';
import { useQuery } from 'react-query';

import {
  AnalysisReportApiResponse,
  AnalysisReportOrderTypeEnum,
  AnalysisReportsCategoriesApiResponse,
  AnalysisReportsProvidersApiResponse,
  AnalysisReportsTypesApiResponse,
  ReportCard,
  ReportsViews,
  useReports,
} from 'features/reports';
import {
  Box,
  Checkbox,
  Col,
  Flex,
  Hr,
  // Input,
  Label,
  Loading,
  Page,
  Paging,
  Row,
  SearchBar,
  SearchSelect,
  Section,
  Tabs,
  // Select,
  Typography,
} from 'components';
import { useSchool } from 'features/school';
import { createUrl, request } from 'app/utils';
import { useParams, useSearchParams } from 'react-router-dom';
import { useResources } from 'features/resources';

import usestorage from 'app/utils/storage';

const ReportsList = () => {
  const [loading, setLoading] = useState(true);
  const [waiting, setWaiting] = useState(false);
  const [view, setView] = useState<ReportsViews>('grid');

  const { reportCategoryUuid } = useParams();

  const storage = usestorage('localStorage');
  const currentDashboard = storage.get('currentDashboard');

  const [searchParams] = useSearchParams();
  const currentDashboardArea = searchParams.get('dashboardAreaUuid');

  const { data: analysisReportsTypes } = useQuery('getAnalysisReportsTypes', async () => {
    const response = await request<AnalysisReportsTypesApiResponse>({
      url: '/api/v1/School/AnalysisReport/Type',
      baseURL: apiGatewayUrl,
      method: 'get',
      params: {
        ...(currentDashboard ? { dashboardUuid: currentDashboard } : {}),
        ...(currentDashboardArea ? { dashboardAreaUuid: currentDashboardArea } : {}),
      },
    });

    return response.data;
  });

  const { data: analysisReportsProviders } = useQuery('getAnalysisReportsProviders', async () => {
    const response = await request<AnalysisReportsProvidersApiResponse>({
      url: '/api/v1/School/AnalysisReport/Provider',
      baseURL: apiGatewayUrl,
      method: 'get',
      params: {
        ...(currentDashboard ? { dashboardUuid: currentDashboard } : {}),
        ...(currentDashboardArea ? { dashboardAreaUuid: currentDashboardArea } : {}),
      },
    });

    return response.data;
  });

  const { data: analysisReportsCategories } = useQuery('getAnalysisReportsCategories', async () => {
    const response = await request<AnalysisReportsCategoriesApiResponse>({
      url: '/api/v1/School/AnalysisReport/Category',
      baseURL: apiGatewayUrl,
      method: 'get',
      params: {
        ...(currentDashboard ? { dashboardUuid: currentDashboard } : {}),
        ...(currentDashboardArea ? { dashboardAreaUuid: currentDashboardArea } : {}),
      },
    });

    return response.data;
  });

  const { apiGatewayUrl } = useSchool();
  const {
    reports,
    currentPage,
    totalPages,
    itensPerPage,
    setReports,
    // downloadLoading,
    // downloadPercentage,
  } = useReports();

  const {
    usingReports,
    schools: { schoolBenndorf, schoolMPC },
  } = useResources();

  const [order, setOrder] = useState<AnalysisReportOrderTypeEnum>(AnalysisReportOrderTypeEnum.Unspecified);
  const [searchTerm, setSearchTerm] = useState('');
  const [reportTypes, setReportTypes] = useState<string[]>([]);
  const [reportProviders, setReportProviders] = useState<string[]>([]);
  const [reportAnalysts] = useState<string[]>([]);
  // const [reportCategoryUuid, setReportCategories] = useState<string[]>([]);

  const [page, setPage] = useState<number>(1);
  const [size, setSize] = useState<number>(50);

  const fetchReports = useCallback(async () => {
    setWaiting(true);

    await Promise.all([
      request({
        url: '/api/v1/School/AnalysisReport/Search',
        method: 'post',
        baseURL: apiGatewayUrl,
        data: {
          beginDate: moment(1641006000).toISOString(),
          endDate: moment().toISOString(),
          searchText: searchTerm,
          analysisReportTypes: reportTypes,
          analysisReportProviders: reportProviders,
          analysisReportAnalysts: reportAnalysts,
          analysisReportCategories: reportCategoryUuid ? [reportCategoryUuid] : [],
          order,
          page,
          size,
        },
        params: {
          ...(currentDashboard ? { dashboardUuid: currentDashboard } : {}),
          ...(currentDashboardArea ? { dashboardAreaUuid: currentDashboardArea } : {}),
        },
      }).then((response: AxiosResponse<AnalysisReportApiResponse>) => {
        setReports(response.data);
      }),
    ]).then(() => {
      setWaiting(false);
      setLoading(false);
    });
  }, [searchTerm, reportCategoryUuid, reportTypes, reportProviders, reportAnalysts, page, size, order, currentDashboard, currentDashboardArea]);

  useEffect(() => {
    fetchReports();
  }, [searchTerm, reportCategoryUuid, reportTypes, reportProviders, reportAnalysts, page, size, order, currentDashboard, currentDashboardArea]);

  let title = 'Relatórios';
  //   let description = 'Tenha acesso aos melhores relatórios das principais casas de análise do mercado.';
  let description = '';

  if (schoolBenndorf) {
    description = '';
  } else if (schoolMPC) {
    title = 'Histórico';
    description = '';
  }

  const onSearchSubmit = () => {};

  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadPercentage, setDownloadPercentage] = useState(0);

  const downloadReport = (downloadUrl: string, downloadFileName: string) => {
    setDownloadLoading(true);

    request({
      url: `/api/v1/School/AnalysisReport/Download/${downloadUrl}`,
      method: 'get',
      baseURL: apiGatewayUrl,
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        const { loaded, total } = progressEvent;
        let percentCompleted = total === undefined ? 0 : Math.floor((loaded / total) * 100);

        setDownloadPercentage(percentCompleted);
      },
    })
      .then((response: AxiosResponse<Blob>) => {
        saveAs(response.data, downloadFileName);

        setDownloadLoading(false);

        setTimeout(() => {
          setDownloadPercentage(0);
        }, 0);
      })
      .catch((error) => {
        setDownloadLoading(false);
        setDownloadPercentage(0);
      });
  };

  const handleReportTypeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setReportTypes((prev) => [...prev, event.target.value]);
    } else {
      setReportTypes((prev) => prev.filter((reportType) => reportType !== event.target.value));
    }
  }, []);

  const handleReportProviderChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setReportProviders((prev) => [...prev, event.target.value]);
    } else {
      setReportProviders((prev) => prev.filter((reportProvider) => reportProvider !== event.target.value));
    }
  }, []);

  const MoreFilter = useCallback(
    () => (
      <>
        <Flex width={{ m: 277, t: 300 }} flexDirection="column">
          {((schoolBenndorf && Number(analysisReportsProviders?.providers?.length) > 1) || !schoolBenndorf) && (
            <>
              <Flex p={3} flexDirection="column">
                <Typography type="type3" upper fontWeight="bold" mb={3}>
                  Casas de análises
                </Typography>

                {analysisReportsProviders?.providers.map((reportProvider, index) => {
                  const checked = reportProviders.includes(String(reportProvider.uuid));

                  return (
                    <Flex key={`checkbox-reportsproviders-${reportProvider.uuid}-${index.toString()}`} flexWrap="nowrap" mt={index ? 2 : 0}>
                      <Flex flexGrow={0} pt={1}>
                        <Checkbox
                          id={`checkbox-reportsproviders-${reportProvider.uuid}-${index.toString()}`}
                          color="gray_500"
                          checkboxSize="small"
                          value={reportProvider.uuid}
                          checked={checked}
                          onChange={handleReportProviderChange}
                        />
                      </Flex>

                      <Box flexGrow={1} ml={2}>
                        <Label htmlFor={`checkbox-reportsproviders-${reportProvider.uuid}-${index.toString()}`}>
                          <Typography color="gray_500">{reportProvider.name}</Typography>
                        </Label>
                      </Box>
                    </Flex>
                  );
                })}
              </Flex>
              <Hr size="2px" m={0} />
            </>
          )}

          <Flex p={3} flexDirection="column">
            <Typography type="type3" upper fontWeight="bold" mb={3}>
              Tipos de relatórios
            </Typography>

            {analysisReportsTypes?.types.map((reportType, index) => {
              const checked = reportTypes.includes(String(reportType.uuid));

              return (
                <Flex key={`checkbox-reportstypes-${reportType.uuid}-${index.toString()}`} flexWrap="nowrap" mt={index ? 2 : 0}>
                  <Flex flexGrow={0} pt={1}>
                    <Checkbox
                      id={`checkbox-reportstypes-${reportType.uuid}-${index.toString()}`}
                      color="gray_500"
                      checkboxSize="small"
                      value={reportType.uuid}
                      checked={checked}
                      onChange={handleReportTypeChange}
                    />
                  </Flex>

                  <Box flexGrow={1} ml={2}>
                    <Label htmlFor={`checkbox-reportstypes-${reportType.uuid}-${index.toString()}`}>
                      <Typography color="gray_500">{reportType.name}</Typography>
                    </Label>
                  </Box>
                </Flex>
              );
            })}
          </Flex>
        </Flex>
      </>
    ),
    [analysisReportsProviders, reportProviders, analysisReportsTypes, reportTypes]
  );

  useEffect(() => {
    if (!usingReports) window.location.href = createUrl('dashboard');
  }, [usingReports]);

  if (!usingReports || loading) return <Loading label="Por favor, aguarde." />;

  return (
    <Page title={title}>
      {downloadLoading && <Loading alpha={0.8} label={`Por favor, aguarde.<br />${downloadPercentage ? `${downloadPercentage}%` : ``}`} />}

      {waiting && <Loading alpha={0.8} />}

      <Flex bg="white" flexDirection="column">
        <Flex flexGrow={0} bg="gray_200">
          <Flex flexDirection="column" py={{ m: 4, d: 8 }} px={{ m: 6, d: 6 }}>
            <Typography type="type8" fontWeight="bold">
              {title}
            </Typography>

            {!!description && <Typography mt={2}>{description}</Typography>}
          </Flex>
        </Flex>

        {/* <Flex flexGrow={0} bg="gray_100">
          <Section>
            <ReportCategories
              categories={analysisReportsCategories?.categories || []}
              setCategory={(category: string) => setReportCategories(category ? [category] : [])}
            />
          </Section>
        </Flex> */}

        {!!analysisReportsCategories?.categories?.length && (
          <Tabs
            items={
              analysisReportsCategories?.categories?.length
                ? [
                    {
                      label: 'Todas as publicações',
                      link: createUrl('reportsList'),
                    },
                    ...analysisReportsCategories?.categories?.map((category) => ({
                      label: category.name,
                      link: `${createUrl('reportsByCategoryList', [category.uuid])}`,
                    })),
                  ]
                : []
            }
          />
        )}

        <Flex bg="white" flexGrow={0} flexDirection="column">
          <Section py={{ m: 30, t: 60 }} flexDirection="column">
            <SearchBar
              searchText={searchTerm}
              onSearchTextChange={setSearchTerm}
              totalPages={totalPages}
              onSearchSubmit={onSearchSubmit}
              currentPage={currentPage}
              selectedRows={false}
              setPage={setPage}
              filters={[
                {
                  selectedValue: order,
                  onChange: (changedOrder) => {
                    setOrder(Number(changedOrder) as AnalysisReportOrderTypeEnum);
                  },
                  options: schoolMPC
                    ? [
                        { value: AnalysisReportOrderTypeEnum.Unspecified, label: 'Ordenar por' },
                        {
                          value: AnalysisReportOrderTypeEnum.WrittenAtDescending,
                          label: 'Data da análise',
                        },
                        {
                          value: AnalysisReportOrderTypeEnum.TypeAscending,
                          label: 'Tipo de análise',
                        },
                        {
                          value: AnalysisReportOrderTypeEnum.NameAscending,
                          label: 'Nome da análise',
                        },
                      ]
                    : [
                        { value: AnalysisReportOrderTypeEnum.Unspecified, label: 'Ordenar por' },
                        {
                          value: AnalysisReportOrderTypeEnum.WrittenAtDescending,
                          label: 'Data do relatório',
                        },
                        {
                          value: AnalysisReportOrderTypeEnum.TypeAscending,
                          label: 'Tipo de relatório',
                        },
                        {
                          value: AnalysisReportOrderTypeEnum.ProviderAscending,
                          label: 'Casa de análise',
                        },
                        {
                          value: AnalysisReportOrderTypeEnum.NameAscending,
                          label: 'Título do relatório',
                        },
                      ],
                  minWidth: 230,
                },
              ]}
              actions={[
                {
                  icon: 'gridView',
                  onClick: () => setView('grid'),
                  label: 'Visualizar em grid',
                  active: view === 'grid',
                },
                {
                  icon: 'sequentialView',
                  onClick: () => setView('list'),
                  label: 'Visualizar em lista',
                  active: view === 'list',
                },
              ]}
              more={schoolMPC ? undefined : <MoreFilter />}
            />

            {reports.length ? (
              <Row mt={3} gutter={20}>
                {reports.map((report, index) => (
                  <Col key={`report-item-${index.toString()}`} width={view === 'grid' ? { m: 1, t: 1 / 2, d: 1 / 4 } : 1} mt={20}>
                    <ReportCard report={report} view={view} downloadReport={downloadReport} />
                  </Col>
                ))}
              </Row>
            ) : (
              <Typography mt={5} fontWeight="bold">
                Nenhum relatório ou análise com essas características.
              </Typography>
            )}

            <Flex justifyContent="space-between" mt={{ m: 4, t: 6 }} flexDirection={{ m: 'column', t: 'row' }}>
              <Box flexGrow={0} mb={{ m: 3, t: 0 }}>
                <SearchSelect
                  defaultValue={itensPerPage}
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                    const { value: filterValue } = event.target;
                    if (filterValue) {
                      setSize(Number(filterValue));
                    }
                  }}
                >
                  {[
                    { value: 20, label: '20 registros' },
                    { value: 50, label: '50 registros' },
                    { value: 100, label: '100 registros' },
                  ].map((filterOption, filterOptionIndex) => (
                    <option
                      // selected={filter.selectedValue === filterOption.value}
                      key={`filterOptionItem-${filterOption.toString()}-${filterOptionIndex.toString()}`}
                      value={filterOption.value}
                    >
                      {filterOption.label}
                    </option>
                  ))}
                </SearchSelect>
              </Box>

              <Flex flexGrow={0} height={50}>
                <Paging totalPages={totalPages} onSearchSubmit={onSearchSubmit} currentPage={currentPage} setPage={setPage} />
              </Flex>
            </Flex>
          </Section>
        </Flex>
      </Flex>
    </Page>
  );
};

export default ReportsList;
